<template>
  <div class="account-panel">

    <!-- key forces a full remount: a keyless patch reuses the signed-out node
         and GSI's async-injected button markup survives inside .user-card. -->
    <div v-if="user" class="acct-in" key="signed-in">
      <div class="user-card">
        <div class="user-avatar">{{ initials }}</div>
        <div class="user-info">
          <span class="user-name">{{ user.name || user.email }}</span>
          <span class="user-email">{{ user.email }}</span>
        </div>
      </div>

      <div class="sync-status" :class="'sync-status--' + syncStatus">
        <span class="sync-dot"></span>
        {{ syncLabel }}
      </div>
      <button
        v-if="syncStatus === 'error'"
        type="button"
        class="btn btn-secondary btn-sm retry-btn"
        @click="retrySync"
      >Retry sync</button>

      <button type="button" class="btn btn-secondary btn-block" @click="signOut">Sign out</button>
      <button type="button" class="btn btn-ghost btn-sm btn-block danger-link" @click="confirmDelete">Delete account and data</button>
    </div>

    <div v-else class="acct-out" key="signed-out">
      <div class="acct-out__icon">
        <i class="fas fa-sync-alt"></i>
      </div>
      <p class="acct-out__title">Sync your practice</p>
      <p class="acct-out__sub">Sign in to keep your history across all your devices.</p>
      <div v-if="loading" class="acct-loading">Signing in…</div>
      <div v-else ref="gsiButton" class="acct-gsi"></div>
      <p v-if="error" class="acct-error">{{ error }}</p>
      <p class="acct-out__sub"><a href="/privacy" target="_blank" rel="noopener">How we use your data</a></p>
    </div>

    <!-- Outside both branches: sharing works signed out, and this is the only
         place an upload can be taken back. -->
    <div v-if="sharedCount > 0" class="shared-voice">
      <p class="shared-voice__title">
        <i class="fas fa-microphone"></i>
        {{ sharedCount }} shared recording{{ sharedCount === 1 ? '' : 's' }}
      </p>
      <p class="shared-voice__sub">
        Attached to links you sent. Anyone holding one of those links can play it.
      </p>
      <button type="button" class="btn btn-danger btn-sm" :disabled="deletingShared" @click="confirmDeleteShared">
        {{ deletingShared ? 'Deleting…' : 'Delete shared recordings' }}
      </button>
    </div>

  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { listShared, deleteAllShared } from '../../services/shareVoice';

const CLIENT_ID = process.env.GOOGLE_CLIENT_ID || '';

export default {
  name: 'AccountPanel',

  data() {
    return {
      sharedCount: 0,
      deletingShared: false
    };
  },

  computed: {
    ...mapState('auth', ['user', 'loading', 'error', 'syncStatus']),

    initials() {
      if (!this.user) return '';
      const name = (this.user.name || this.user.email || '').trim();
      const words = name.split(/\s+/);
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },

    // Report sync problems as such, never by signing out (see store/modules/auth.js).
    syncLabel() {
      switch (this.syncStatus) {
        case 'syncing': return 'Syncing…';
        case 'error':   return "Couldn't sync - your practice is safe on this device";
        default:        return 'Syncing across your devices';
      }
    }
  },

  watch: {
    user(newVal, oldVal) {
      if (!newVal && !oldVal) return;
      if (!newVal) this.$nextTick(this.renderGsiButton);
    }
  },

  mounted() {
    this._destroyed = false;
    this.sharedCount = listShared().length;
    if (!this.user) this.renderGsiButton();
  },

  beforeDestroy() {
    // renderGsiButton may still be retrying when the modal closes.
    this._destroyed = true;
    if (this._gsiRetryTimer) clearTimeout(this._gsiRetryTimer);
  },

  methods: {
    ...mapActions('auth', ['signIn', 'logout', 'deleteAccount', 'push', 'pull']),

    renderGsiButton() {
      if (this._destroyed || this.user) return;
      var self = this;
      var google = window.google;
      if (!google || !google.accounts || !google.accounts.id) {
        this._gsiRetryTimer = setTimeout(function() { self.renderGsiButton(); }, 500);
        return;
      }
      if (!CLIENT_ID) {
        console.warn('AccountPanel: GOOGLE_CLIENT_ID not set');
        return;
      }
      // initialize() is page-global; per-render calls re-register the callback.
      if (!window.__ezshadowGsiInitialized) {
        google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: function(response) {
            var store = self.$store;
            if (store) store.dispatch('auth/signIn', response.credential);
          }
        });
        window.__ezshadowGsiInitialized = true;
      }
      var el = self.$refs.gsiButton;
      if (!el) return;
      google.accounts.id.renderButton(el, {
        theme: 'filled_black',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width: 240,
        // GSI follows the browser locale otherwise (rendered Japanese in testing).
        locale: 'en'
      });
    },

    retrySync() {
      this.push();
      this.pull();
    },

    signOut() {
      this.logout();
    },

    confirmDelete() {
      if (!window.confirm('Delete your account and all server-side data? Your local history stays on this device.')) return;
      this.deleteAccount();
    },

    async confirmDeleteShared() {
      if (!window.confirm('Delete the recordings you attached to share links? Those links will keep working, just without your voice.')) return;
      this.deletingShared = true;
      try {
        await deleteAllShared();
      } finally {
        // Failed deletes stay listed so the user can retry.
        this.sharedCount = listShared().length;
        this.deletingShared = false;
      }
    }
  }
};
</script>

<style scoped>
.shared-voice {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--line);
}

.shared-voice__title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}

.shared-voice__sub {
  margin: 4px 0 12px;
  font-size: 13px;
  color: var(--muted);
}

.acct-in {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--surface-2);
  border-radius: var(--r-card);
  padding: 14px;
}

.user-avatar {
  width: 44px;
  height: 44px;
  border-radius: var(--r-pill);
  background: var(--accent-soft);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  flex: none;
}

.user-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-email {
  font-size: 13px;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sync-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--pass);
}

.sync-dot {
  width: 6px;
  height: 6px;
  border-radius: var(--r-pill);
  background: currentColor;
  flex: none;
}

.sync-status--syncing { color: var(--muted); }
.sync-status--error   { color: var(--warn); }

.retry-btn { align-self: flex-start; }

.danger-link { color: var(--fail); }
.danger-link:hover { color: var(--fail); background: var(--fail-soft); }

.acct-out {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 12px 0 4px;
}

.acct-out__icon {
  width: 52px;
  height: 52px;
  border-radius: var(--r-pill);
  background: var(--accent-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  font-size: 18px;
  margin-bottom: 4px;
}

.acct-out__title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
  margin: 0;
}

.acct-out__sub {
  font-size: 14px;
  color: var(--muted);
  margin: 0 0 8px;
  max-width: 30ch;
}

.acct-gsi {
  display: flex;
  justify-content: center;
  min-height: 44px;
}

.acct-loading {
  font-size: 14px;
  color: var(--muted);
}

.acct-error {
  font-size: 13px;
  color: var(--fail);
  margin: 0;
}
</style>
